import * as React from 'react';

import { formatPhoneNumber } from '@own-local/ad-utils/lib/formatter';
import TrackingKeys from '@own-local/ad-utils/lib/trackingKeys';
import { Contact, Social, TrackFunction, TranslateFunction } from '@own-local/ad-utils/lib/types';
import { getLinkUrl } from '@own-local/ad-utils/lib/url';
import Address, { urlEncodedAddress } from '@own-local/ad-utils/lib/Address';
import {
  DirectionsIcon,
  FacebookIcon,
  InstagramIcon,
  LinkIcon,
  MapMarkerIcon,
  OpenInNewIcon,
  PhoneIcon,
  PhoneOutgoingIcon,
  TwitterIcon,
  YelpIcon,
} from './Icons';

export interface InfoContentsProps {
  contact: Contact;
  social: Social;
  active: boolean;
  toggleShare: () => void;
  name: string;
  track: TrackFunction;
  locale: string;
  translateString: TranslateFunction;
  gamTrackingUrl?: string;
  mapDisabled: boolean;
  showFacebook: Function;
  showTwitter: Function;
  showShareButtons?: boolean;
}

const InfoContents = (props: InfoContentsProps) => {
  const { name, social, track, translateString, locale, gamTrackingUrl, mapDisabled, showFacebook, showTwitter } = props;
  const { offer_url, twitter, instagram, facebook, yelp } = social;
  const { website, place, phones, noFollowLink } = props.contact;
  const { address1, city, subdivision } = place;

  let cleanWebsite;
  if (website) {
    cleanWebsite = website
      .replace('www.', '')
      .replace('http://', '')
      .replace('https://', '')
      .replace(/\/$/, '');
  }

  // Safe address display
  const showAddress = !!address1 && !!city;
  let addressUrl = '';
  if (showAddress) {
    addressUrl = 'http://maps.google.com?daddr=' + urlEncodedAddress(place);
  }

  const showPhoneNumber = !!phones.length && !!phones[0].number;
  const showShareButtons = props.showShareButtons || (!!offer_url && !!city && !!name && !!subdivision);
  const socialStyles = props.showShareButtons ? { height: 42 } : {};

  const twitterShareLink = `https://twitter.com/intent/tweet?text=${name} in ${city}, ${subdivision}&url=${offer_url}`;
  const facebookShareLink = `https://www.facebook.com/sharer?u=${offer_url}`;

  const directionsTitle = translateString('get_directions', locale, 'Get Directions');
  const phoneTitle = translateString('call_phone', locale, 'Call phone number');
  const websiteTitle = translateString('open_website', locale, 'Open website in a new tab');
  const facebookPageTitle = translateString('open_facebook', locale, 'Open Facebook in a new tab');
  const twitterPageTitle = translateString('open_twitter', locale, 'Open Twitter in a new tab');
  const instagramPageTitle = translateString('open_instagram', locale, 'Open Instagram in a new tab');
  const yelpPageTitle = translateString('open_yelp', locale, 'Open Yelp in a new tab');
  const showShareTitle = translateString('show_share', locale, 'Show Share Buttons');
  const hideShareTitle = translateString('hide_share', locale, 'Hide Share Buttons');

  let callLink;

  if (showPhoneNumber) {
    callLink = `tel:${phones[0].number}`;
  }

  const facebookOnClick = () => {
    showFacebook();
    track(
      TrackingKeys.CONTACT.CATEGORY_NAME,
      TrackingKeys.CONTACT.ACTIONS.FACEBOOK,
      facebook
    );
  }

  const twitterOnClick = () => {
    showTwitter();
    track(
      TrackingKeys.CONTACT.CATEGORY_NAME,
      TrackingKeys.CONTACT.ACTIONS.TWITTER,
      twitter
    );
  }

  return (
    <div className={`content-wrapper info ${props.active && 'content-active'}`}>
      {showAddress &&
        !mapDisabled ? (
          <a
            className="info-block directions-block"
            title={directionsTitle}
            target="_blank"
            href={getLinkUrl(addressUrl, gamTrackingUrl)}
            onClick={() => {
              track(
                TrackingKeys.CONTACT.CATEGORY_NAME,
                TrackingKeys.CONTACT.ACTIONS.OPEN_DIRECTIONS,
                addressUrl
              );
            }}
          >
            <MapMarkerIcon className="mdi" />
            <Address place={place} />
            <DirectionsIcon className="mdi guide" />
          </a>
        ) : !!address1 && (
          <div
            className="info-block directions-block"
            title={directionsTitle}
          >
            <MapMarkerIcon className="mdi" />
            <Address place={place} />
          </div>
        )
      }

      {showPhoneNumber && (
        <a
          className="info-block phone-block"
          title={phoneTitle}
          target="_blank"
          href={getLinkUrl(callLink, gamTrackingUrl)}
          onClick={() => {
            track(TrackingKeys.CONTACT.CATEGORY_NAME, TrackingKeys.CONTACT.ACTIONS.CALL, callLink);
          }}
        >
          <PhoneIcon className="mdi" />
          <span className="text">{formatPhoneNumber(phones[0].number, place.country)}</span>
          <PhoneOutgoingIcon className="mdi guide" />
        </a>
      )}
      {website && (
        <a
          className="info-block website-block"
          title={websiteTitle}
          target="_blank"
          href={getLinkUrl(website, gamTrackingUrl)}
          rel={noFollowLink ? "nofollow" : undefined}
          onClick={() => {
            track(
              TrackingKeys.CONTACT.CATEGORY_NAME,
              TrackingKeys.CONTACT.ACTIONS.WEBSITE,
              website
            );
          }}
        >
          <LinkIcon className="mdi" />
          <span className="text">{cleanWebsite}</span>
          <OpenInNewIcon className="mdi guide" />
        </a>
      )}
      <div className="social-links" style={socialStyles}>
        {facebook && (
          <a
            className="facebook-button"
            title={facebookPageTitle}
            target="_blank"
            href={getLinkUrl(facebook, gamTrackingUrl)}
            onClick={facebookOnClick}
          >
            <FacebookIcon/>
          </a>
        )}
        {twitter && (
          <a
            className="twitter-button"
            title={twitterPageTitle}
            target="_blank"
            href={getLinkUrl(twitter, gamTrackingUrl)}
            onClick={twitterOnClick}
          >
            <TwitterIcon/>
          </a>
        )}
        {instagram && (
          <a
            className="instagram-button"
            title={instagramPageTitle}
            target="_blank"
            href={getLinkUrl(instagram, gamTrackingUrl)}
            onClick={() => {
              track(
                TrackingKeys.CONTACT.CATEGORY_NAME,
                TrackingKeys.CONTACT.ACTIONS.INSTAGRAM,
                instagram
              );
            }}
          >
            <InstagramIcon/>
          </a>
        )}
        {yelp && (
          <a
            className="yelp-button"
            title={yelpPageTitle}
            target="_blank"
            href={getLinkUrl(yelp, gamTrackingUrl)}
            onClick={() => {
              track(TrackingKeys.CONTACT.CATEGORY_NAME, TrackingKeys.CONTACT.ACTIONS.YELP, yelp);
            }}
          >
            <YelpIcon/>
          </a>
        )}
      </div>
      {showShareButtons && (
        <div>
          <span
            className="share-toggle share-toggle-more mdi mdi-share-variant"
            title={showShareTitle}
            onClick={props.toggleShare}
          />
          <span
            className="share-toggle share-toggle-close mdi mdi-close"
            title={hideShareTitle}
            onClick={props.toggleShare}
          />
        </div>
      )}
      <button className="share-modal inactive">
        {website && (
          <a
            className="share-facebook"
            target="_blank"
            href={getLinkUrl(facebookShareLink, gamTrackingUrl)}
            onClick={() => {
              track(
                TrackingKeys.SHARE.CATEGORY_NAME,
                TrackingKeys.SHARE.ACTIONS.FACEBOOK,
                facebookShareLink
              );
            }}
          >
            <FacebookIcon
              fill="#FFFFFF"
              style={{ paddingRight: '6px', lineHeight: '30px', marginBottom: '-5px' }}
            />
            Share
          </a>
        )}
        {showShareButtons && (
          <a
            className="share-twitter"
            target="_blank"
            href={getLinkUrl(twitterShareLink, gamTrackingUrl)}
            onClick={() => {
              track(
                TrackingKeys.SHARE.CATEGORY_NAME,
                TrackingKeys.SHARE.ACTIONS.TWITTER,
                twitterShareLink
              );
            }}
          >
            <TwitterIcon
              fill="#FFFFFF"
              style={{ paddingRight: '6px', lineHeight: '30px', marginBottom: '-5px' }}
            />
            Tweet
          </a>
        )}
      </button>
    </div>
  );
};

export default InfoContents;
